"use client"

import { useState, useEffect } from "react"
import { useNavigate, Link, useSearchParams } from "react-router-dom"
import { api } from "../services/api"
import GoogleLoginButton from "./GoogleLoginButton"
import { FiEye, FiEyeOff, FiShield, FiSmartphone, FiWifi, FiWifiOff } from "react-icons/fi"
import { useAuth } from "../contexts/AuthContext"
import useSecretTapTrigger from "../hooks/useSecretTapTrigger"
import { isMobileWebShortcutEnabled, openAdminQuickLogin } from "../utils/adminSecretAccess"

function Login() {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    role: "client",
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [biometricAvailable, setBiometricAvailable] = useState(false)
  const [showTwoFA, setShowTwoFA] = useState(false)
  const [twoFACode, setTwoFACode] = useState("")
  const [loginAttempts, setLoginAttempts] = useState(0)
  const [isBlocked, setIsBlocked] = useState(false)
  const [blockTimeRemaining, setBlockTimeRemaining] = useState(0)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { login } = useAuth()
  const [mobileAdminShortcut, setMobileAdminShortcut] = useState(false)
  const onTitleSecretTap = useSecretTapTrigger(openAdminQuickLogin)

  useEffect(() => {
    const sync = () => setMobileAdminShortcut(isMobileWebShortcutEnabled())
    sync()
    const mqNarrow = window.matchMedia("(max-width: 768px)")
    const mqCoarse = window.matchMedia("(pointer: coarse)")
    mqNarrow.addEventListener("change", sync)
    mqCoarse.addEventListener("change", sync)
    return () => {
      mqNarrow.removeEventListener("change", sync)
      mqCoarse.removeEventListener("change", sync)
    }
  }, [])

  useEffect(() => {
    // Vérifier la disponibilité de l'authentification biométrique
    if ("credentials" in navigator && "create" in navigator.credentials) {
      setBiometricAvailable(true)
    }

    // Gérer le statut en ligne/hors ligne
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    // Vérifier les tentatives de connexion précédentes
    const attempts = localStorage.getItem("loginAttempts")
    const lastAttempt = localStorage.getItem("lastLoginAttempt")

    if (attempts && Number.parseInt(attempts) >= 5) {
      const timeDiff = Date.now() - Number.parseInt(lastAttempt)
      if (timeDiff < 900000) {
        // 15 minutes
        setIsBlocked(true)
        setBlockTimeRemaining(Math.ceil((900000 - timeDiff) / 1000))
      } else {
        localStorage.removeItem("loginAttempts")
        localStorage.removeItem("lastLoginAttempt")
      }
    }

    const error = searchParams.get("error")
    const verified = searchParams.get("verified")
    
    if (verified === "true") {
      setError("success:Votre adresse email a été vérifiée avec succès ! Vous pouvez maintenant vous connecter.")
    } else if (error === "google_auth_failed") {
      setError("La connexion avec Google a échoué. Veuillez réessayer.")
    }

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [searchParams])

  // Timer pour le déblocage
  useEffect(() => {
    let interval
    if (isBlocked && blockTimeRemaining > 0) {
      interval = setInterval(() => {
        setBlockTimeRemaining((prev) => {
          if (prev <= 1) {
            setIsBlocked(false)
            localStorage.removeItem("loginAttempts")
            localStorage.removeItem("lastLoginAttempt")
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => clearInterval(interval)
  }, [isBlocked, blockTimeRemaining])

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
    setError("")
  }

  const handleBiometricLogin = async () => {
    try {
      setIsLoading(true)
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: new Uint8Array(32),
          allowCredentials: [
            {
              id: new Uint8Array(64),
              type: "public-key",
              transports: ["internal", "usb", "nfc", "ble"],
            },
          ],
          timeout: 60000,
          userVerification: "required",
        },
      })

      if (credential) {
        // Simuler une connexion biométrique réussie
        const userData = { id: 1, email: "user@example.com", role: formData.role }
        await login(userData, "biometric-token")
        navigate(`/${formData.role}`)
      }
    } catch (error) {
      setError("Authentification biométrique échouée")
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (isBlocked) {
      setError(`Compte temporairement bloqué. Réessayez dans ${Math.ceil(blockTimeRemaining / 60)} minutes.`)
      return
    }

    setIsLoading(true)
    setError("")

    try {
      const { email, password, role } = formData
      const data = await api.login(email, password, role)

      // Vérifier si 2FA est requis
      if (data.requiresTwoFA) {
        setShowTwoFA(true)
        setIsLoading(false)
        return
      }

      await login(data.user, data.token)

      // Réinitialiser les tentatives de connexion
      localStorage.removeItem("loginAttempts")
      localStorage.removeItem("lastLoginAttempt")

      // Géolocalisation et redirection
      try {
        const getPosition = () =>
          new Promise((resolve, reject) => {
            if (!("geolocation" in navigator)) return reject(new Error("Géolocalisation indisponible"))
            navigator.geolocation.getCurrentPosition(
              (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
              (err) => reject(err),
              { enableHighAccuracy: true, timeout: 8000 },
            )
          })
        const { lat, lng } = await getPosition()

        let city = null,
          country = null
        try {
          const resp = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}`,
          )
          const json = await resp.json()
          const addr = json && json.address ? json.address : {}
          city = addr.city || addr.town || addr.village || addr.municipality || null
          country = addr.country || null
        } catch (_) {}
        await api.upsertMyLocation({ lat, lng, city, country })
      } catch (geoErr) {
        console.warn("Localisation non mise à jour:", geoErr?.message || geoErr)
      }

      switch (data.user.role) {
        case "admin":
          navigate("/admin")
          break
        case "client":
          navigate("/client")
          break
        case "demenageur":
          navigate("/demenageur")
          break
        default:
          navigate("/")
      }
    } catch (error) {
      const attempts = Number.parseInt(localStorage.getItem("loginAttempts") || "0") + 1
      localStorage.setItem("loginAttempts", attempts.toString())
      localStorage.setItem("lastLoginAttempt", Date.now().toString())

      setLoginAttempts(attempts)

      if (attempts >= 5) {
        setIsBlocked(true)
        setBlockTimeRemaining(900) // 15 minutes
        setError("Trop de tentatives échouées. Compte bloqué pendant 15 minutes.")
      } else {
        setError(error.message || "Une erreur est survenue lors de la connexion. Veuillez réessayer.")
      }
      console.error("Login error:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleTwoFASubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const data = await api.verifyTwoFA(formData.email, twoFACode)
      await login(data.user, data.token)
      navigate(`/${data.user.role}`)
    } catch (error) {
      setError("Code 2FA invalide")
    } finally {
      setIsLoading(false)
    }
  }

  if (showTwoFA) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center mb-4 shadow-lg">
              <FiShield className="text-2xl text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Authentification à deux facteurs</h2>
            <p className="text-gray-400 mb-6">Entrez le code envoyé sur votre téléphone</p>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-xl py-8 px-6 shadow-2xl rounded-2xl border border-amber-500/20">
            <form onSubmit={handleTwoFASubmit} className="space-y-6">
              <div>
                <label htmlFor="twofa" className="block text-sm font-medium text-gray-300 mb-2">
                  Code de vérification
                </label>
                <input
                  id="twofa"
                  name="twofa"
                  type="text"
                  required
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 text-white placeholder-gray-400 text-center text-2xl tracking-widest"
                  placeholder="000000"
                  maxLength="6"
                />
              </div>

              {error && (
                <div className="bg-red-900/50 border border-red-500/50 rounded-lg p-4">
                  <p className="text-sm text-red-300">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-amber-600 hover:to-amber-700 transition-all duration-300 transform hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isLoading ? "Vérification..." : "Vérifier"}
              </button>
            </form>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Indicateur de connexion */}
        <div className="fixed top-4 right-4 z-50">
          <div
            className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium ${
              isOnline
                ? "bg-green-900/50 text-green-300 border border-green-500/30"
                : "bg-red-900/50 text-red-300 border border-red-500/30"
            }`}
          >
            {isOnline ? <FiWifi className="w-4 h-4" /> : <FiWifiOff className="w-4 h-4" />}
            {isOnline ? "En ligne" : "Hors ligne"}
          </div>
        </div>

        {/* Logo et titre */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center mb-4 shadow-lg">
            <span className="text-2xl text-white font-bold">🏠</span>
          </div>
          <h2
            role={mobileAdminShortcut ? "button" : undefined}
            tabIndex={mobileAdminShortcut ? -1 : undefined}
            onClick={mobileAdminShortcut ? onTitleSecretTap : undefined}
            className="text-3xl font-bold text-white mb-2 select-none touch-manipulation"
          >
            Connexion Liberty
          </h2>
          <p className="text-gray-400 mb-6">Accédez à votre espace personnel</p>

          {/* Authentification biométrique */}
          {biometricAvailable && (
            <button
              onClick={handleBiometricLogin}
              disabled={isLoading || isBlocked}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-gray-800/50 border border-amber-500/30 rounded-lg shadow-sm text-sm font-medium text-gray-300 hover:bg-gray-700/50 hover:border-amber-500/50 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all duration-300 mb-4 disabled:opacity-50"
            >
              <FiSmartphone className="h-5 w-5 text-amber-400" />
              <span>Connexion biométrique</span>
            </button>
          )}

          {/* Bouton Google */}
          <div className="mb-6">
            <GoogleLoginButton text="Continuer avec Google" fullWidth />
          </div>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-900 text-gray-500">Ou continuez avec</span>
            </div>
          </div>
        </div>

        {/* Formulaire de connexion */}
        <div className="bg-gray-800/50 backdrop-blur-xl py-8 px-6 shadow-2xl rounded-2xl border border-amber-500/20">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Type d'utilisateur */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">Type de compte</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: "client", label: "Client", icon: "👤" },
                  { value: "demenageur", label: "Déménageur", icon: "🚚" },
                ].map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, role: type.value })}
                    className={`p-3 rounded-lg border-2 transition-all duration-300 ${
                      formData.role === type.value
                        ? "border-amber-500 bg-amber-500/10 text-amber-400"
                        : "border-gray-600 hover:border-amber-500/50 hover:bg-gray-700/50 text-gray-400"
                    }`}
                  >
                    <div className="text-2xl mb-1">{type.icon}</div>
                    <div className="text-xs font-medium">{type.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Adresse email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 hover:border-amber-500/50 text-white placeholder-gray-400"
                placeholder="votre@email.com"
              />
            </div>

            {/* Mot de passe */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 pr-12 bg-gray-700/50 border border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 hover:border-amber-500/50 text-white placeholder-gray-400"
                  placeholder="Votre mot de passe"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-amber-400 transition-colors duration-300"
                >
                  {showPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Indicateur de tentatives */}
            {loginAttempts > 0 && loginAttempts < 5 && (
              <div className="bg-yellow-900/50 border border-yellow-500/50 rounded-lg p-3 mb-4">
                <p className="text-sm text-yellow-300">
                  Tentative {loginAttempts}/5. {5 - loginAttempts} tentatives restantes.
                </p>
              </div>
            )}
            
            {/* Messages d'erreur et de succès */}
            {error && (
              <div className={`mb-4 p-4 rounded ${
                error.startsWith('success:') 
                  ? 'bg-green-100 border-l-4 border-green-500 text-green-700' 
                  : 'bg-red-100 border-l-4 border-red-500 text-red-700'
              }`}>
                <p>{error.startsWith('success:') ? error.substring(8) : error}</p>
              </div>
            )}

            {/* Bouton de connexion */}
            <button
              type="submit"
              disabled={isLoading || isBlocked}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-amber-600 hover:to-amber-700 transition-all duration-300 transform hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Connexion en cours...
                </div>
              ) : isBlocked ? (
                `Bloqué (${Math.ceil(blockTimeRemaining / 60)}min)`
              ) : (
                "Se connecter"
              )}
            </button>
          </form>

          {/* Liens utiles */}
          <div className="mt-6 text-center space-y-2">
            <div>
              <a
                href="/"
                className="text-amber-400 hover:text-amber-300 text-sm font-medium transition-colors duration-300"
              >
                ← Retour à l'accueil
              </a>
            </div>
            <div className="mb-2">
              <Link
                to="/forgot-password"
                className="text-sm text-amber-400 hover:text-amber-300 font-medium transition-colors duration-300"
              >
                Mot de passe oublié ?
              </Link>
            </div>
            <div>
              <p className="text-sm text-gray-400">
                Pas encore de compte ?{" "}
                <Link
                  to="/register"
                  className="text-amber-400 hover:text-amber-300 font-medium transition-colors duration-300"
                >
                  Créer un compte
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Informations supplémentaires */}
        <div className="text-center">
          <p className="text-sm text-gray-400">
            Besoin d'aide ?{" "}
            <a
              href="/contact"
              className="text-amber-400 hover:text-amber-300 font-medium transition-colors duration-300"
            >
              Contactez-nous
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default Login
